// Seven-layer validation panel with per-layer status pills and drawer opener.
import React from "react";
import type { LucideIcon } from "lucide-react";
import type {
  LayerKey,
  LayerRunState,
} from "../../../stores/processingJobStore";
import { Button } from "../../components/ui/button";
import { LAYER_RUN_STATUS, LAYER_ROW_META } from "./constants";
import { resolveLayerRowState } from "./utils";

export interface LayerRow {
  key: string;
  label: string;
  icon: LucideIcon;
  cleared: boolean;
  evaluated: boolean;
}

export function ConsoleSevenLayerPanel({
  layerRows,
  layerRuns,
  layersSettled,
  layersErrored,
  layersNotEvaluated,
  layersTotal,
  onOpenDrawer,
}: {
  layerRows: LayerRow[];
  layerRuns?: Partial<Record<LayerKey, LayerRunState>>;
  layersSettled: boolean;
  layersErrored: boolean;
  layersNotEvaluated: number;
  layersTotal: number;
  onOpenDrawer: (key: LayerKey) => void;
}) {
  return (
    <>
      {layersNotEvaluated > 0 && (
        <div className="mx-4 mt-2 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50/70 px-2 py-1.5 text-[10px] leading-snug text-amber-800 dark:border-amber-500/25 dark:bg-amber-500/[0.06] dark:text-amber-300">
          <span className="mt-0.5 size-1 flex-none rounded-full bg-amber-500" />
          <span>
            {layersNotEvaluated} of {layersTotal} layer
            {layersTotal === 1 ? "" : "s"} couldn&apos;t be assessed.
            Your document didn&apos;t contain enough relevant content
            for {layersNotEvaluated === 1 ? "it" : "them"}. Please
            review the document and add the missing information.
          </span>
        </div>
      )}
      <div className="flex flex-col gap-1.5 px-4 pb-3 pt-2.5 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
        {layerRows.map((layer) => {
          const cleared = layer.cleared;
          const Icon = layer.icon;
          const evaluated = layer.evaluated;
          const layerRun = layerRuns?.[layer.key as LayerKey];
          const isRunning = layerRun?.status === LAYER_RUN_STATUS.RUNNING;
          const isComplete = layerRun?.status === LAYER_RUN_STATUS.COMPLETE;
          const isLayerError = layerRun?.status === LAYER_RUN_STATUS.ERROR;
          const notEvaluated = layersSettled && !evaluated;

          const rowState = resolveLayerRowState({
            isRunning,
            isComplete,
            isLayerError,
            layersErrored,
            layersSettled,
            notEvaluated,
            cleared,
          });
          const { chipTone, pillTone, pillLabel } = LAYER_ROW_META[rowState];

          const rowTitle = isLayerError
            ? layerRun?.error
            : notEvaluated
              ? "Your document didn't contain enough relevant content for this layer to be assessed. Please review the document."
              : undefined;

          const canOpenDrawer =
            isComplete &&
            (layer.key === "domain" ||
              layer.key === "concept" ||
              layer.key === "requirement" ||
              layer.key === "relationship" ||
              layer.key === "consistency" ||
              layer.key === "quality" ||
              layer.key === "governance");
          const rowContent = (
            <>
              <span
                className={`grid size-7 flex-none place-items-center rounded-lg border transition-colors ${chipTone}`}
              >
                <Icon
                  className={`size-3.5 ${isRunning ? "animate-pulse" : ""}`}
                />
              </span>
              <span
                className={`min-w-0 flex-1 break-words text-[11.5px] ${
                  notEvaluated
                    ? "text-muted-foreground"
                    : layersSettled || layersErrored || isRunning || isComplete
                      ? "text-foreground"
                      : "text-muted-foreground"
                }`}
              >
                {layer.label}
                {isRunning && layerRun?.note && (
                  <span className="mt-0.5 block break-words text-[10px] text-muted-foreground">
                    {layerRun.note}
                  </span>
                )}
              </span>
              <span
                className={`flex-none rounded border px-1.5 py-0.5 text-[9.5px] ${pillTone}`}
              >
                {pillLabel}
              </span>
            </>
          );

          return (
            <div key={layer.key} title={rowTitle}>
              {canOpenDrawer ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="none"
                  onClick={() => onOpenDrawer(layer.key as LayerKey)}
                  className="flex w-full items-center justify-start gap-2.5 rounded-md p-1 text-left font-normal"
                >
                  {rowContent}
                </Button>
              ) : (
                <div className="flex items-center gap-2.5 rounded-md p-1 font-normal">
                  {rowContent}
                </div>
              )}
              {isRunning && (
                <div className="mt-1 h-1 overflow-hidden rounded-full bg-[#e2e8f0] dark:bg-white/[0.08]">
                  <span
                    className="block h-full w-1/3 rounded-full bg-indigo-500"
                    style={{
                      animation:
                        "rgc-indeterminate 1.6s ease-in-out infinite",
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
